/**
 * The only browser storage this app writes for auth.
 *
 * The old app AES-encrypted the whole user object into localStorage using a key
 * shipped in the bundle (`VITE_CRYPTO_KEY`), which protects nothing — anyone who
 * can read localStorage can read the key next to it. The real credential was
 * always an httpOnly cookie the JS never sees.
 *
 * So nothing sensitive is persisted. All that is kept is a boolean hint used to
 * avoid flashing the login screen on reload, plus the path to return to. Both
 * are namespaced so `logOut()` never has to reach for `localStorage.clear()`
 * and take the theme and sidebar preferences down with it.
 */

const SESSION_HINT_KEY = "im.hadSession";
const REDIRECT_PATH_KEY = "im.redirectPath";
const LOGGED_OUT_KEY = "im.loggedOut";

const AUTH_KEYS = [SESSION_HINT_KEY, REDIRECT_PATH_KEY, LOGGED_OUT_KEY];

function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode, blocked cookies) — auth still works */
  }
}

function remove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

export const hadSession = () => read(SESSION_HINT_KEY) === "1";
export const markSession = () => write(SESSION_HINT_KEY, "1");

export const getRedirectPath = () => read(REDIRECT_PATH_KEY);
export const setRedirectPath = (path) => write(REDIRECT_PATH_KEY, path);
export const clearRedirectPath = () => remove(REDIRECT_PATH_KEY);

export const wasLoggedOut = () => read(LOGGED_OUT_KEY) === "1";
export const markLoggedOut = () => write(LOGGED_OUT_KEY, "1");
export const clearLoggedOut = () => remove(LOGGED_OUT_KEY);

/**
 * The single predicate for "should we try to restore a session on load".
 *
 * The slice's initial status and the bootstrap both need this answer and must
 * never disagree: a `loading` initial state with no request behind it would hang
 * the app on the spinner forever.
 */
export const shouldRestoreSession = () => hadSession() && !wasLoggedOut();

/** Clears only this app's auth keys — never unrelated preferences. */
export function clearAuthStorage() {
  AUTH_KEYS.forEach(remove);
}
